import React from 'react';
import FileUploadSection from './FileUploadSection';

const ReceivableForm = ({
  mode,
  paymentData,
  customerInfo,
  loadInfo,
  register,
  errors,
  watch,
  setValue,
  handleSubmit,
  images,
  pdfs,
  existingImages,
  existingPdfs,
  handleImageChange,
  handlePdfChange,
  removeImage,
  removePdf,
  removeExistingImage,
  removeExistingPdf,
  receivableStatuses,
  paymentMethods,
  error,
  isSubmitting,
  onCancel,
  onSave
}) => {
  const isEditMode = mode === 'edit';
  const formValues = watch();

  return (
    <form onSubmit={handleSubmit(onSave)} className="payment-wizard__payment-form-expanded">
      <div className="payment-wizard__form-section">
        <h4 className="payment-wizard__form-title">Payment Receivable</h4>
        
        {/* Customer Info - Read-only */}
        {customerInfo && (
          <div className="payment-wizard__load-info payment-wizard__info-readonly">
            <h5>Customer Information</h5>
            <p><strong>Company Name:</strong> {customerInfo.companyName || '--'}</p>
            <p><strong>Email:</strong> {customerInfo.email || customerInfo.emails?.[0] || '--'}</p>
            <p><strong>Phone:</strong> {customerInfo.phone || customerInfo.phoneNumber || '--'}</p>
            {customerInfo.address && (
              <p><strong>Address:</strong> {typeof customerInfo.address === 'object' 
                ? `${customerInfo.address.address || ''}, ${customerInfo.address.city || ''}, ${customerInfo.address.state || ''} ${customerInfo.address.zipCode || ''}`.trim()
                : customerInfo.address}</p>
            )}
            {customerInfo.customerAddress && (
              <p><strong>Address:</strong> {[
                customerInfo.customerAddress.address,
                customerInfo.customerAddress.city,
                customerInfo.customerAddress.state,
                customerInfo.customerAddress.zipCode
              ].filter(Boolean).join(', ') || '--'}</p>
            )}
            {(customerInfo.paymentMethod || customerInfo.paymentTerms || customerInfo.creditLimit !== undefined) && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                <p style={{ marginBottom: '8px', fontWeight: 'bold', color: '#666' }}>Payment Information:</p>
                {customerInfo.paymentMethod && (
                  <p><strong>Payment Method:</strong> {customerInfo.paymentMethod}</p>
                )}
                {customerInfo.paymentTerms && (
                  <p><strong>DTP:</strong> {customerInfo.paymentTerms}</p>
                )}
                {customerInfo.creditLimit !== undefined && customerInfo.creditLimit !== null && (
                  <p><strong>Credit Limit:</strong> ${typeof customerInfo.creditLimit === 'number' 
                    ? customerInfo.creditLimit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : parseFloat(customerInfo.creditLimit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  }</p>
                )}
              </div>
            )}
          </div>
        )}

        {loadInfo && (
          <div className="payment-wizard__load-info payment-wizard__info-readonly">
            <h5>Load Information</h5>
            <p><strong>Order ID:</strong> {loadInfo.orderId || loadInfo._id || '--'}</p>
            <p><strong>Customer Rate:</strong> ${loadInfo.customerRate || paymentData?.customerRate || '0'}</p>
            <p><strong>Status:</strong> {loadInfo.status || '--'}</p>
          </div>
        )}

        {/* Read-only fields in edit mode */}
        {isEditMode && (
          <>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Amount</label>
              <input
                type="text"
                value={`$${paymentData?.amount || '0'}`}
                className="form-control"
                disabled
                readOnly
              />
            </div>
            <div className="payment-wizard__form-row">
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Due Date</label>
                <input
                  type="text"
                  value={paymentData?.dueDate ? new Date(paymentData.dueDate).toLocaleDateString() : '--'}
                  className="form-control"
                  disabled
                  readOnly
                />
              </div>
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Payment Method</label>
                <input
                  type="text"
                  value={paymentData?.paymentMethod || '--'}
                  className="form-control"
                  disabled
                  readOnly
                />
              </div>
            </div>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Notes</label>
              <textarea
                value={paymentData?.notes || ''}
                className="form-control"
                rows={3}
                disabled
                readOnly
              />
            </div>
          </>
        )}

        {/* Editable fields in add mode */}
        {!isEditMode && (
          <>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Amount *</label>
              <input
                type="number"
                {...register("amount", {
                  required: "Amount is required",
                  min: { value: 0.01, message: "Amount must be greater than 0" }
                })}
                className={`form-control ${errors.amount ? 'input-error' : ''}`}
                placeholder="Enter amount"
              />
              {errors.amount && (
                <span className="field-error">{errors.amount.message}</span>
              )}
            </div>
            <div className="payment-wizard__form-row">
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Due Date</label>
                <input
                  type="date"
                  {...register("dueDate")}
                  className="form-control"
                />
              </div>
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Payment Method</label>
                <select
                  {...register("paymentMethod")}
                  className="form-select"
                >
                  <option value="">Select method</option>
                  {paymentMethods.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Notes</label>
              <textarea
                {...register("notes")}
                className="form-control"
                rows={3}
                placeholder="Enter notes..."
              />
            </div>
          </>
        )}

        {/* Status - Always editable */}
        <div className="payment-wizard__form-field">
          <label className="payment-wizard__form-label">Status</label>
          <select
            {...register("status")}
            className="form-select"
          >
            {receivableStatuses.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* File Uploads */}
        <FileUploadSection
          title="Images"
          accept="image/*"
          files={images}
          existingFiles={existingImages}
          handleChange={handleImageChange}
          removeFile={removeImage}
          removeExisting={removeExistingImage}
        />

        <FileUploadSection
          title="PDFs"
          accept=".pdf"
          files={pdfs}
          existingFiles={existingPdfs}
          handleChange={handlePdfChange}
          removeFile={removePdf}
          removeExisting={removeExistingPdf}
        />
      </div>

      {error && <div className="payment-wizard__error">{error}</div>}

      <div className="payment-wizard__form-actions">
        <button
          className="payment-wizard__cancel-btn btn"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="payment-wizard__save-btn btn" 
          disabled={isSubmitting || (!isEditMode && !formValues.amount)}
        >
          {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}
        </button>
      </div>
    </form>
  );
};

export default ReceivableForm;

